import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { authApi, type AuthUser } from '@/api/auth.api';
import { clearSession } from '@/lib/apiClient';

type AuthValue = {
  user: AuthUser | null;
  loading: boolean;
  login: (usernameOrEmail: string, password: string) => Promise<AuthUser>;
  refreshUser: () => Promise<AuthUser>;
  logout: () => Promise<void>;
  can: (permission: string) => boolean;
};

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(() => Boolean(localStorage.getItem('vendiz.accessToken')));

  useEffect(() => {
    let active = true;
    if (!localStorage.getItem('vendiz.accessToken')) return;
    authApi.me().then((profile) => { if (active) setUser(profile); }).catch(() => clearSession()).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const expire = () => { setUser(null); setLoading(false); };
    window.addEventListener('vendiz:session-expired', expire);
    return () => window.removeEventListener('vendiz:session-expired', expire);
  }, []);

  const login = useCallback(async (usernameOrEmail: string, password: string) => {
    const session = await authApi.login({ usernameOrEmail, password });
    localStorage.setItem('vendiz.accessToken', session.accessToken);
    localStorage.setItem('vendiz.refreshToken', session.refreshToken);
    setUser(session.user);
    return session.user;
  }, []);

  const refreshUser = useCallback(async () => {
    const profile = await authApi.me();
    setUser(profile);
    return profile;
  }, []);

  const logout = useCallback(async () => {
    try { await authApi.logout(); } finally { setUser(null); }
  }, []);

  const value = useMemo<AuthValue>(() => ({ user, loading, login, refreshUser, logout, can: (permission) => Boolean(user?.permissions.includes(permission)) }), [loading, login, refreshUser, logout, user]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
